import React, { useState } from "react";

const SelectMemberTypeModal = ({ 
  isOpen, 
  onClose, 
  onSelectExisting, 
  onAddNew,
  existingMembers,
  accountOwnerIds = [],
  excludeMemberIds = [],
  type // 'beneficiary' or 'trusted_contact'
}) => {
  const [step, setStep] = useState('choose'); // 'choose' or 'select_existing'
  const [selectedMemberId, setSelectedMemberId] = useState(null);

  const handleClose = () => {
    setStep('choose');
    setSelectedMemberId(null);
    onClose();
  };

  const handleChooseExisting = () => {
    setStep('select_existing');
  };

  const handleAddNew = () => {
    setStep('choose');
    setSelectedMemberId(null);
    onAddNew();
  };

  const handleSelectMember = (memberId) => {
    setSelectedMemberId(memberId);
  };

  const handleConfirmSelection = () => {
    if (selectedMemberId) {
      const selectedMember = existingMembers.find(m => m.id === selectedMemberId);
      setStep('choose');
      setSelectedMemberId(null);
      onSelectExisting(selectedMember);
    }
  };

  const handleBack = () => {
    setStep('choose');
    setSelectedMemberId(null);
  };

  const availableMembers = existingMembers.filter(
    member => !accountOwnerIds.includes(member.id) && !excludeMemberIds.includes(member.id)
  );

  if (!isOpen) return null;

  const displayType = type === 'beneficiary' ? 'Beneficiary' : 'Trusted Contact';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              {step === 'choose' ? `Add ${displayType}` : `Select ${displayType}`}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6">
          {step === 'choose' ? (
            <div className="space-y-4">
              <p className="text-gray-600 mb-6">
                Would you like to choose from existing household members or add a new member?
              </p>

              <button
                onClick={handleChooseExisting}
                disabled={availableMembers.length === 0}
                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center justify-between group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-200 disabled:hover:bg-white"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Choose from Existing Members</h3>
                    <p className="text-sm text-gray-500">
                      {availableMembers.length === 0 
                        ? 'No available members' 
                        : `Select from ${availableMembers.length} member${availableMembers.length > 1 ? 's' : ''}`}
                    </p>
                  </div>
                </div>
                <svg className="w-6 h-6 text-gray-400 group-hover:text-emerald-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <button
                onClick={handleAddNew}
                className="w-full p-6 border-2 border-gray-200 rounded-lg hover:border-emerald-500 hover:bg-emerald-50 transition-all flex items-center justify-between group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center transition-colors">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                    </svg>
                  </div>
                  <div className="text-left">
                    <h3 className="text-lg font-semibold text-gray-900">Add New Member</h3>
                    <p className="text-sm text-gray-500">Create a new household member</p>
                  </div>
                </div>
                <svg className="w-6 h-6 text-gray-400 group-hover:text-emerald-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700 font-medium mb-4"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <p className="text-gray-600 mb-4">
                Select a household member to add as {displayType.toLowerCase()}
              </p>

              {availableMembers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No available members to select
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {availableMembers.map((member) => {
                    const fullName = [
                      member.personal_info?.first_name,
                      member.personal_info?.middle_name,
                      member.personal_info?.last_name
                    ].filter(Boolean).join(' ') || 'Unnamed Member';

                    const isSelected = selectedMemberId === member.id;

                    return (
                      <button
                        key={member.id}
                        onClick={() => handleSelectMember(member.id)}
                        className={`w-full p-4 border-2 rounded-lg text-left transition-all ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-semibold text-gray-900">{fullName}</h4>
                            {member.personal_info?.email && (
                              <p className="text-sm text-gray-500">{member.personal_info.email}</p>
                            )}
                          </div>
                          {isSelected && (
                            <svg className="w-6 h-6 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={handleBack}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSelection}
                  disabled={!selectedMemberId}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SelectMemberTypeModal;
